import {
  STATES,
  ITEM_KINDS,
  ITEM_DEFINITION_CODES,
  ITEM_DEFINITION_CODES_TO_STATES,
} from '../../../constants';
import { actions } from '../../../state';
import '@updater-components/loading-spinner';
import { UTILITIES_MARKET_BASE_PATH } from './utilities-market/constants';
import { trackEngagement } from '@mover-app/engagement';
import './utilities.global.css';
import { mover } from './common/selectors';
import { ENVIRONMENT } from '../../../environment';
import createClient from './common/store';
import { deepCamelCaseKeys } from './common/utils';

// store the utilities store in memory based on moveId
// since it's pretty heavy for local storage but we want to optimize repeat requests.
const inMemoryUtilsStore = {};

const makeInitialData = (updStore, appShell) => {
  const moverData = mover(updStore.getState());
  const siteBranding = appShell.interactors.siteBranding;
  const siteBrandingData = {
    website: siteBranding.selectors.website(siteBranding.getState()),
    phone: siteBranding.selectors.phone(siteBranding.getState()),
    company: siteBranding.selectors.getCompany(siteBranding.getState()),
    companyName: siteBranding.selectors.getCompanyName(siteBranding.getState()),
  };
  const session = appShell.interactors.session;
  const itemDefinitions = session.selectors.itemDefinitions(session.getState());
  return {
    ...moverData,
    siteBranding: siteBrandingData,
    itemDefinitions: (itemDefinitions || []).map(deepCamelCaseKeys),
  };
};

export function UtilitiesConfig($stateProvider) {
  'ngInject';
  $stateProvider
    // Top most utilites state
    // determine if we will show them the "providers" UI (Legacy)
    // or if the user should be routed into the new "market view" UI
    .state(STATES.STEP_UTILITIES, {
      url: '/utilities',
      disableBreadcrumb: true,
      views: {
        '@': {
          template:
            '<div id="utilities-loading"><upd-loading-spinner></upd-loading-spinner></div>',
          controller: ($state, $scope, updStore, appShell) => {
            'ngInject';
            let destroyedBit = false;
            $scope.$on('$destroy', () => {
              destroyedBit = true;
            });
            trackEngagement({
              code: ITEM_DEFINITION_CODES.UTILITY,
              name: 'Utilities',
            });

            const moverData = makeInitialData(updStore, appShell);

            const utilsStore =
              inMemoryUtilsStore[moverData.move.id] ||
              createClient(
                {
                  env: ENVIRONMENT,
                  sessionHeaders: moverData.sessionHeaders,
                },
                moverData,
                moverData.move.id
              );

            inMemoryUtilsStore[moverData.move.id] = utilsStore;

            utilsStore
              .availableUtilityData()
              .then(data => {
                const markets = (data && data.utilityMarkets) || [];
                const hasMarket = (markets || []).some(
                  market => market && market.enabled
                );

                const subscriptions = (data && data.subscriptions) || {};
                const hasSubscriptions = Object.keys(subscriptions)
                  .filter(key => Array.isArray(subscriptions[key]))
                  .some(
                    kind =>
                      subscriptions[kind] && subscriptions[kind].length > 0
                  );

                const state =
                  hasMarket || hasSubscriptions
                    ? STATES.STEP_UTILITIES_MARKET
                    : STATES.STEP_UTILITIES_PROVIDERS;
                return state;
              })
              .then(state => {
                if (destroyedBit) {
                  return; // don't continue, user bounced.
                }
                updStore.dispatch(
                  actions.router.stateGo(state, null, {
                    location: 'replace',
                  })
                );
              })
              .catch(ex => {
                updStore.dispatch(actions.router.stateGo(STATES.ERROR));
                throw ex;
              });
          },
        },
      },
      analytics: {
        ignore: true,
      },
      scrollTop: true,
      data: {
        moveItem: {
          type: ITEM_KINDS.UTILITY,
          singleton: true,
        },
      },
    })
    // Providers = Contact cards for Electric, Water, and Gas.
    .state(STATES.STEP_UTILITIES_PROVIDERS, {
      url: '/utilities/providers',
      views: {
        '@': {
          template: '<upd-utilities></upd-utilities>',
        },
      },
      title: 'Contact Utilities',
      titleBar: {
        title: 'Contact Utilities',
        save: 'Average Time Saved: 35 Minutes',
        icon: 'utilities',
      },
      analytics: {
        page: 'Utilities',
      },
      scrollTop: true,
      data: {
        moveItem: {
          type: ITEM_KINDS.UTILITY,
          singleton: true,
        },
      },
    })
    // Market = Ecommerce experience for deregulated markets
    .state(STATES.STEP_UTILITIES_MARKET, {
      url: `${UTILITIES_MARKET_BASE_PATH}/*path?`,
      disableBreadcrumb: true,
      title: 'Contact Utilities',
      views: {
        '@': {
          template: `<div id="upd-utilities-market" class="micro-app-fixes">
            <div id="utilities-loading"><upd-loading-spinner></upd-loading-spinner></div>
          </div>`,
          controller: (
            updEnvironment,
            updStore,
            $element,
            $scope,
            appShell
          ) => {
            'ngInject';
            const utilitiesMarketChunk = import(/* webpackChunkName: "utilities-market" */ './utilities-market');
            // console.log('[DEBUG] entering the controller');
            utilitiesMarketChunk.then(({ utilitiesMarketApp }) => {
              const utilitiesAppElement = $element[0].querySelector(
                '#upd-utilities-market'
              );

              const parentRouter = {
                PATHS: {
                  NEXT_STEP: STATES.NEXT_STEP,
                },
                go: path => {
                  updStore.dispatch(actions.router.stateGo(path));
                },
              };

              const moverData = makeInitialData(updStore, appShell);

              const appDestroy = utilitiesMarketApp(utilitiesAppElement, {
                basePath: UTILITIES_MARKET_BASE_PATH,
                moverData,
                parentRouter,
                updStore,
                store: inMemoryUtilsStore[moverData.move.id],
              });

              $scope.$on('$destroy', () => {
                appDestroy();
              });
            });
          },
        },
      },
      analytics: {
        ignore: true,
      },
    });
}
